import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { fetchUserInfo } from "../services/overviewApiService";
import { useOktaAuth } from "@okta/okta-react";
import demoStudentJson from "@/data/demoStudent.json";
import {
    buildTeacherUserApiFallback,
    shouldUseTeacherUserApiFallback,
} from "@/utils/teacherUserApiFallback";

export interface TeacherSchool {
    schoolId: string;
    schoolName: string;
    schoolShortName: string;
}

function normalizeApiSchool(raw: Record<string, unknown>, index: number): TeacherSchool | null {
    const schoolId = String(raw.schoolId ?? raw.id ?? raw.school_id ?? "");
    if (!schoolId) return null;
    return {
        schoolId,
        schoolName: String(raw.schoolName ?? raw.name ?? raw.school_name ?? `School ${index + 1}`),
        schoolShortName: String(raw.schoolShortName ?? raw.abbreviation ?? raw.school_short_name ?? ""),
    };
}

function schoolsFromStudentList(studentsList: Array<Record<string, unknown>>): TeacherSchool[] {
    const bySchool = new Map<string, TeacherSchool>();
    for (const s of studentsList) {
        const schoolId = String(s.schoolId ?? "");
        if (!schoolId || bySchool.has(schoolId)) continue;
        bySchool.set(schoolId, {
            schoolId,
            schoolName: String(s.schoolName ?? ""),
            schoolShortName: String(s.schoolShortName ?? ""),
        });
    }
    return Array.from(bySchool.values());
}

function parseTeacherSchoolsFromUserPayload(data: {
    students?: { studentsList?: unknown[]; schoolName?: string; schoolShortName?: string } | null;
    schools?: unknown;
    user?: { schoolId?: string };
}): TeacherSchool[] {
    const studentsList = data.students?.studentsList ?? [];

    if (Array.isArray(data.schools) && data.schools.length > 0) {
        return data.schools
            .map((raw, i) => normalizeApiSchool(raw as Record<string, unknown>, i))
            .filter((s): s is TeacherSchool => s !== null);
    }

    const nested = (data.schools as { schoolsList?: unknown[] } | null | undefined)?.schoolsList;
    if (Array.isArray(nested) && nested.length > 0) {
        return nested
            .map((raw, i) => normalizeApiSchool(raw as Record<string, unknown>, i))
            .filter((s): s is TeacherSchool => s !== null);
    }

    if (studentsList.length > 0) {
        return schoolsFromStudentList(studentsList as Array<Record<string, unknown>>);
    }

    if (data.user?.schoolId) {
        return [
            {
                schoolId: String(data.user.schoolId),
                schoolName: data.students?.schoolName || "School",
                schoolShortName: data.students?.schoolShortName || "",
            },
        ];
    }

    return [];
}

function buildTeacherPreviewStudent(school: TeacherSchool, index: number): Student {
    const demoList =
        (demoStudentJson as { students?: { studentsList?: Record<string, unknown>[] } }).students
            ?.studentsList ?? [];
    const demo = demoList[0];
    const base = demo
        ? coerceStudentFromPayload(demo, index, school.schoolId)
        : {
              accountId: 710000,
              hasFlexProgram: false,
              identityId: "",
              firstName: "Student",
              lastName: "",
              nickName: null,
              gradeId: "5",
              schoolName: school.schoolName,
              schoolShortName: school.schoolShortName,
              schoolTypeConfigured: "public",
              schoolId: school.schoolId,
              entryDate: "",
              age: 0,
          };

    return {
        ...base,
        accountId: Number(base.accountId) + index,
        identityId: `teacher-school-${school.schoolId}`,
        schoolId: school.schoolId,
        schoolName: school.schoolName,
        schoolShortName: school.schoolShortName,
        schoolTypeConfigured: "public",
    };
}

function coerceStudentFromPayload(
    raw: Record<string, unknown>,
    index: number,
    defaultSchoolId: string
): Student {
    const schoolId = String(raw.schoolId ?? "").trim() || defaultSchoolId;
    return {
        accountId: Number(raw.accountId ?? index),
        hasFlexProgram: Boolean(raw.hasFlexProgram ?? false),
        identityId: String(raw.identityId ?? ""),
        firstName: String(raw.firstName ?? ""),
        lastName: String(raw.lastName ?? ""),
        nickName: raw.nickName != null && raw.nickName !== "" ? String(raw.nickName) : null,
        gradeId: String(raw.gradeId ?? ""),
        schoolName: String(raw.schoolName ?? ""),
        schoolShortName: String(raw.schoolShortName ?? ""),
        schoolTypeConfigured: String(raw.schoolTypeConfigured ?? "public"),
        schoolId,
        entryDate: raw.entryDate != null ? String(raw.entryDate) : "",
        age: Number(raw.age ?? 0),
    };
}

function teacherStudentsFromDemoJson(userSchoolIdFallback: string): Student[] {
    const demo = demoStudentJson as {
        students?: { studentsList?: Record<string, unknown>[] };
        user?: { schoolId?: string };
    };
    const list = demo.students?.studentsList ?? [];
    const sid = String(demo.user?.schoolId ?? userSchoolIdFallback ?? "");
    return list.map((r, i) => coerceStudentFromPayload(r as Record<string, unknown>, i, sid));
}

function parseRoleId(rawRoleId: unknown): number | null {
    if (typeof rawRoleId === "number" && !Number.isNaN(rawRoleId)) {
        return rawRoleId;
    }
    if (rawRoleId != null && rawRoleId !== "") {
        const n = Number(rawRoleId);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}

export interface Student {
    accountId: number;
    hasFlexProgram: boolean;
    identityId: string;
    firstName: string;
    lastName: string;
    nickName: string | null;
    gradeId: string;
    schoolName: string;
    schoolShortName: string;
    schoolTypeConfigured: string;
    schoolId: string;
    roleId?: number;
    entryDate: string;
    age: number;
}

interface OverviewContextType {
    loggedInUser: any;
    students: Student[];
    teacherSchools: TeacherSchool[];
    schoolFullName: string;
    formattedStudentNames: string;
    userRoleVal: string;
    userRoleId: number | null;
    hasNonHighSchoolStudents: boolean;
    identityId: string | null;
    schoolShortName: string;
    schoolTypeConfigured: string;
    loading: boolean;
    error: string | null;
}

const OverviewContext = createContext<OverviewContextType | undefined>(undefined);
export const OverviewProvider = ({ children }: { children: ReactNode }) => {
    const { authState } = useOktaAuth();
    const [loggedInUser, setLoggedInUser] = useState<any>(null);
    const [students, setStudents] = useState<any[]>([]);
    const [schoolFullName, setSchoolFullName] = useState<string>("");
    const [schoolShortName, setSchoolShortName] = useState<string>("");

    const [formattedStudentNames, setFormattedStudentNames] = useState<string>('');
    const [userRoleVal, setUserRole] = useState<string>('');
    const [userRoleId, setUserRoleId] = useState<number | null>(null);
    const [identityId, setIdentityId] = useState<string | null>(null);

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [hasNonHighSchoolStudents, setHasNonHighSchoolStudents] = useState<boolean>(false);
    const [schoolTypeConfigured, setSchoolTypeConfigured] = useState<string>("");
    const [teacherSchools, setTeacherSchools] = useState<TeacherSchool[]>([]);

    useEffect(() => {
        if (!authState) {
            return;
        }
        if (!authState.isAuthenticated) {
            setLoading(false);
            return;
        }
        const accessToken = authState.accessToken?.accessToken;
        if (!accessToken) {
            setError("No accessToken found with Oauth");
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        const fetchData = async () => {
            try {
                let data = await fetchUserInfo(accessToken);
                if (!data?.user && shouldUseTeacherUserApiFallback(authState, true)) {
                    console.warn(
                        "[OverviewContext] GET /user failed; using teacher demo fallback payload."
                    );
                    data = buildTeacherUserApiFallback(authState);
                }
                if (!data?.user) {
                    setError("Failed to fetch user info from API.");
                    return;
                }

                const normalizedRole = String(data.user.role ?? "").trim().toUpperCase();

                setLoggedInUser(data.user);
                setUserRole(normalizedRole);
                setUserRoleId(parseRoleId(data.user?.roleId));
                setIdentityId(data.user.identityId || null);

                const studentsList = data.students?.studentsList ?? [];

                if (normalizedRole === "TEACHER") {
                    const schools = parseTeacherSchoolsFromUserPayload(data);
                    const apiStudentRows = Array.isArray(studentsList) ? studentsList : [];
                    const defaultSchoolId = String(data.user?.schoolId ?? "");

                    let nextTeacherSchools: TeacherSchool[] = [];
                    if (schools.length > 0) {
                        nextTeacherSchools = schools;
                    } else if (apiStudentRows.length > 0) {
                        nextTeacherSchools = schoolsFromStudentList(
                            apiStudentRows as Array<Record<string, unknown>>
                        );
                    } else {
                        const demoStudents = teacherStudentsFromDemoJson(defaultSchoolId);
                        nextTeacherSchools = schoolsFromStudentList(
                            demoStudents as unknown as Array<Record<string, unknown>>
                        );
                    }

                    const nextStudents = nextTeacherSchools.map((sch, idx) =>
                        buildTeacherPreviewStudent(sch, idx)
                    );

                    setTeacherSchools(nextTeacherSchools);
                    setStudents(nextStudents);

                    const headSchool = nextTeacherSchools[0];
                    const headStudent = nextStudents[0];
                    if (headSchool) {
                        setSchoolFullName(headSchool.schoolName);
                        setSchoolShortName(headSchool.schoolShortName);
                        setSchoolTypeConfigured("public");
                    } else if (headStudent) {
                        setSchoolFullName(headStudent.schoolName);
                        setSchoolShortName(headStudent.schoolShortName);
                        setSchoolTypeConfigured(headStudent.schoolTypeConfigured || "public");
                    } else {
                        setSchoolFullName("");
                        setSchoolShortName("");
                        setSchoolTypeConfigured("");
                    }
                    setHasNonHighSchoolStudents(false);
                } else {
                    setTeacherSchools([]);
                    setStudents(Array.isArray(studentsList) ? studentsList : []);

                    setSchoolFullName(data.students?.schoolName || "");
                    setSchoolShortName(data.students?.schoolShortName || "");
                    setSchoolTypeConfigured(data.students?.schoolTypeConfigured || "");

                    const nonHighSchoolStudents = data.students?.studentsList?.some(
                        (student: Student) => Number(student.gradeId) < 9
                    );
                    setHasNonHighSchoolStudents(!!nonHighSchoolStudents);
                }
            } catch (err) {
                console.error(err);
                setError("Failed to fetch user info from API.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [authState]);

    useEffect(() => {
        setFormattedStudentNames(formatStudentNames(students));
    }, [students]);

    const formatStudentNames = (students: any[]): string => {
        const names = students.map((student) => student.firstName);
        if (names.length === 0) return '';
        if (names.length === 1) return `${names[0]}'s`;
        if (names.length === 2) return `${names[0]} and ${names[1]}'s`;
        return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}'s`;
    };

    return (
        <OverviewContext.Provider value={{ loggedInUser, students, teacherSchools, schoolFullName, schoolShortName, schoolTypeConfigured, formattedStudentNames: formatStudentNames(students), userRoleVal, userRoleId, identityId, hasNonHighSchoolStudents, loading, error }}>
            {children}
        </OverviewContext.Provider>
    );
}

export const useOverviewContext = () => {
    const context = useContext(OverviewContext);
    if (!context) {
        throw new Error("useOverviewContext must be used within an OverviewProvider");
    }
    return context;
};
